import { useQueryClient } from '@tanstack/react-query';
import type { AxiosProgressEvent } from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  getApiV1TutoraiDocumentsQueryOptions,
  usePostApiV1TutoraiDocumentsClassstudentid,
  usePostApiV1TutoraiDocumentsDocumentidImport,
} from '@/shared/api/generated';
import type { TutorDocument } from '../types/tutorAi';
import { CONTEXT_FILE_ACCEPT, CONTEXT_FILE_MAX_SIZE_BYTES } from '../utils/tutorAiLimits';

/**
 * Arquivo recusado na validação de cliente, com o motivo.
 *
 * `duplicate` é nome já presente na turma — ou repetido dentro do próprio lote. O servidor
 * aceitaria: criaria um segundo documento e cobraria os tokens de embedding de novo, deixando
 * o mesmo conteúdo em dobro no contexto do tutor. A recusa é só do cliente.
 */
export interface RejectedFile {
  readonly name: string;
  readonly reason: 'type' | 'size' | 'duplicate';
}

/** Consolidado de um lote de upload, para a tela reportar num toast só. */
export interface UploadOutcome {
  readonly acceptedCount: number;
  readonly importedCount: number;
  readonly chunksGenerated: number;
  readonly embedTokensUsed: number;
  /** Nomes dos arquivos que não chegaram ao servidor. */
  readonly uploadFailures: readonly string[];
  /** Nomes dos arquivos que subiram mas não foram indexados — ficam Pendentes. */
  readonly importFailures: readonly string[];
}

/** Situação de um arquivo dentro do lote. */
export type UploadItemStatus =
  | 'queued'
  | 'uploading'
  | 'importing'
  | 'done'
  | 'uploadFailed'
  | 'importFailed';

/**
 * Um arquivo do lote e sua situação atual.
 *
 * A lista nasce com todos os arquivos aceitos em `queued` e só então o laço começa, para o
 * operador ver de imediato o que foi aceito — antes, o item existia apenas para o arquivo em
 * processamento e sumia ao terminar, então um lote de cinco parecia um de um.
 *
 * `percent` só é significativo em `uploading`, onde vem do `onUploadProgress` do Axios; nas
 * demais situações é `0`. `importing` não tem percentual porque
 * `POST /documents/{id}/import` é uma chamada única no servidor (chunking + embedding), sem
 * eventos intermediários — a UI mostra um indicador indeterminado em vez de inventar um
 * número.
 *
 * As duas falhas são distintas porque as consequências diferem: `uploadFailed` não criou
 * nada, enquanto `importFailed` deixou o documento em Pendente — retentável pela ação
 * Importar da linha, sem reenviar o arquivo.
 */
export interface UploadProgressItem {
  /** Posição no lote. Identidade estável: o nome pode repetir dentro de um mesmo drop. */
  readonly id: number;
  readonly name: string;
  readonly status: UploadItemStatus;
  readonly percent: number;
}

/**
 * Atualiza uma entrada do lote por `id`, preservando as demais.
 *
 * Existe porque cada transição agora mexe em um item de uma lista que persiste, não mais
 * substitui uma lista de um elemento só.
 *
 * @param items - Lista atual
 * @param id - Posição do item a alterar
 * @param patch - Campos a sobrescrever
 * @returns Nova lista, com apenas o item alvo alterado
 */
export function patchUploadItem(
  items: readonly UploadProgressItem[],
  id: number,
  patch: Partial<UploadProgressItem>,
): readonly UploadProgressItem[] {
  return items.map(item => (item.id === id ? { ...item, ...patch } : item));
}

/**
 * Converte um evento de progresso do Axios em percentual inteiro de 0 a 100.
 *
 * `total` é opcional no `AxiosProgressEvent`: quando o servidor não informa o tamanho do
 * corpo — ou informa zero — dividir por ele produziria `NaN` ou `Infinity`, que na barra
 * apareceria como preenchimento quebrado. Nesse caso o percentual é `0`: a barra fica parada
 * em vez de exibir um valor inventado, e o operador ainda vê o nome do arquivo e o status.
 *
 * Exportada porque é a única parte do progresso testável isoladamente — o callback em si é
 * registrado no cliente Axios, e no jsdom com MSW os eventos de progresso do XHR não são
 * emitidos de forma confiável.
 *
 * @param event - Evento de progresso, do qual só `loaded` e `total` importam
 * @returns Percentual de 0 a 100, ou `0` quando o total é desconhecido
 *
 * @example
 * ```ts
 * computeUploadPercent({ loaded: 512, total: 1024 }); // 50
 * computeUploadPercent({ loaded: 512 });              // 0
 * ```
 */
export function computeUploadPercent(event: Pick<AxiosProgressEvent, 'loaded' | 'total'>): number {
  if (!event.total) return 0;
  return Math.round((event.loaded / event.total) * 100);
}

const ACCEPTED_EXTENSIONS = CONTEXT_FILE_ACCEPT.split(',');

function isAcceptedType(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return ACCEPTED_EXTENSIONS.some(extension => lower.endsWith(extension));
}

/**
 * Chave de comparação de nome de arquivo: sem espaços nas bordas, sem distinção de caixa.
 *
 * Sem distinção de caixa porque `Ementa.md` e `ementa.md` são o mesmo documento para o
 * operador — e, em Windows, nem seriam dois arquivos diferentes na origem.
 */
function fileNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/** Identidade estável para o default de `existingDocuments`: `[]` literal recriaria o Set a cada render. */
const NO_DOCUMENTS: readonly TutorDocument[] = [];

/**
 * Upload de documentos de contexto com indexação automática.
 *
 * Cada arquivo aceito passa por duas chamadas em sequência: `POST /documents/{classStudentId}`
 * e, com o id devolvido, `POST /documents/{documentId}/import`. O processamento é
 * sequencial, não paralelo, porque a importação consome tokens de embedding e disparar
 * várias de uma vez tornaria o custo de um lote imprevisível.
 *
 * Os dois modos de falha são reportados separados porque têm consequências diferentes:
 * falha de upload não cria nada, enquanto falha de import deixa o documento existindo em
 * `Pending` — e nesse caso a ação Importar da linha é o retry, sem precisar reenviar o
 * arquivo.
 *
 * `items` dá visibilidade ao operador durante o lote: a lista nasce com todos os arquivos
 * aceitos e cada um carrega sua própria situação, então um lote de cinco aparece como cinco
 * linhas desde o início. O processamento continua sendo um de cada vez — só a visibilidade é
 * do lote inteiro. A lista esvazia ao fim do lote.
 *
 * `existingDocuments` é o que já está na turma, usado só para recusar nome repetido. É
 * parâmetro em vez de uma consulta interna para o hook não depender do GET de documentos:
 * quem chama já tem a lista carregada. Consequência aceita: enquanto a lista não chegou (ou
 * falhou), a checagem não tem contra o que comparar e o envio passa — como passava antes desta
 * regra. O servidor aceita o duplicado de qualquer forma, então falhar aberto aqui só devolve
 * o comportamento antigo, em vez de bloquear um envio legítimo por causa de outro endpoint.
 *
 * @param classStudentId - Turma que recebe os documentos
 * @param existingDocuments - Documentos já na turma, para recusar nome repetido
 * @returns A ação de upload, se um lote está em andamento e os itens do lote com a situação
 * de cada arquivo
 *
 * @example
 * ```tsx
 * const { documents } = useTutorClassDocuments(9020);
 * const { upload, isUploading, items } = useTutorDocumentUpload(9020, documents);
 * const { rejected, outcome } = await upload(files);
 * ```
 */
export function useTutorDocumentUpload(
  classStudentId: number,
  existingDocuments: readonly TutorDocument[] = NO_DOCUMENTS,
) {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [items, setItems] = useState<readonly UploadProgressItem[]>([]);

  // Exatamente um upload está em voo por vez (laço sequencial abaixo) — a ref existe porque
  // `onUploadProgress` é configurado uma vez no nível do hook (não por chamada) e precisa
  // saber a qual item do lote atribuir o percentual recebido. `-1` = nenhum em voo.
  const uploadingItemIdRef = useRef(-1);

  const existingNames = useMemo(
    () => new Set(existingDocuments.map(document => fileNameKey(document.originalFileName))),
    [existingDocuments],
  );

  const uploadMutation = usePostApiV1TutoraiDocumentsClassstudentid({
    client: {
      onUploadProgress: (event: AxiosProgressEvent) => {
        const id = uploadingItemIdRef.current;
        if (id < 0) return;
        setItems(current => patchUploadItem(current, id, { percent: computeUploadPercent(event) }));
      },
    },
  });
  const importMutation = usePostApiV1TutoraiDocumentsDocumentidImport();

  const upload = useCallback(
    async (incoming: File[]) => {
      const rejected: RejectedFile[] = [];
      const accepted: File[] = [];
      // Nomes aceitos neste lote. O mesmo arquivo solto duas vezes num único drop não chega
      // a existir na turma na hora da checagem — sem isto, as duas cópias passariam.
      const acceptedNames = new Set<string>();

      for (const file of incoming) {
        if (!isAcceptedType(file.name)) {
          rejected.push({ name: file.name, reason: 'type' });
          continue;
        }
        if (file.size > CONTEXT_FILE_MAX_SIZE_BYTES) {
          rejected.push({ name: file.name, reason: 'size' });
          continue;
        }
        const key = fileNameKey(file.name);
        if (existingNames.has(key) || acceptedNames.has(key)) {
          rejected.push({ name: file.name, reason: 'duplicate' });
          continue;
        }
        acceptedNames.add(key);
        accepted.push(file);
      }

      if (accepted.length === 0) {
        return {
          rejected,
          outcome: {
            acceptedCount: 0,
            importedCount: 0,
            chunksGenerated: 0,
            embedTokensUsed: 0,
            uploadFailures: [],
            importFailures: [],
          } satisfies UploadOutcome,
        };
      }

      setIsUploading(true);
      setItems(
        accepted.map((file, index) => ({
          id: index,
          name: file.name,
          status: 'queued' as const,
          percent: 0,
        })),
      );

      const uploadFailures: string[] = [];
      const importFailures: string[] = [];
      let importedCount = 0;
      let chunksGenerated = 0;
      let embedTokensUsed = 0;

      try {
        for (const [index, file] of accepted.entries()) {
          let documentId: number | undefined;

          uploadingItemIdRef.current = index;
          setItems(current => patchUploadItem(current, index, { status: 'uploading', percent: 0 }));

          try {
            const created = await uploadMutation.mutateAsync({
              classStudentId,
              data: { file },
            });
            documentId = created?.id;
          } catch {
            uploadFailures.push(file.name);
            setItems(current =>
              patchUploadItem(current, index, { status: 'uploadFailed', percent: 0 }),
            );
            continue;
          } finally {
            uploadingItemIdRef.current = -1;
          }

          if (documentId === undefined) {
            // Upload respondeu 2xx sem id: não há o que importar. Contabilizado como falha de
            // importação porque é onde o `UploadOutcome` já o coloca, e a consequência para o
            // operador é a mesma.
            importFailures.push(file.name);
            setItems(current =>
              patchUploadItem(current, index, { status: 'importFailed', percent: 0 }),
            );
            continue;
          }

          setItems(current => patchUploadItem(current, index, { status: 'importing', percent: 0 }));

          try {
            const result = await importMutation.mutateAsync({ documentId });
            importedCount += 1;
            chunksGenerated += result?.chunksGenerated ?? 0;
            embedTokensUsed += result?.embedTokensUsed ?? 0;
            setItems(current => patchUploadItem(current, index, { status: 'done', percent: 0 }));
          } catch {
            importFailures.push(file.name);
            setItems(current =>
              patchUploadItem(current, index, { status: 'importFailed', percent: 0 }),
            );
          }
        }
      } finally {
        // `finally`, não uma linha solta após o laço: cada erro por arquivo já é capturado
        // acima, mas um `throw` que escape do laço por qualquer outro motivo não pode deixar
        // `isUploading` travado em `true` — a dropzone recusaria todo novo arquivo até um
        // recarregamento. `items` some junto: nenhum arquivo deve seguir "em progresso" numa
        // tela sem lote em andamento.
        setIsUploading(false);
        setItems([]);
      }

      void queryClient.invalidateQueries(getApiV1TutoraiDocumentsQueryOptions({ classStudentId }));

      return {
        rejected,
        outcome: {
          acceptedCount: accepted.length,
          importedCount,
          chunksGenerated,
          embedTokensUsed,
          uploadFailures,
          importFailures,
        } satisfies UploadOutcome,
      };
    },
    [classStudentId, existingNames, importMutation, queryClient, uploadMutation],
  );

  return { upload, isUploading, items } as const;
}
